// Small parts of Admin › OTA Releases › Changelog. Nothing here is under
// 13 px (AGENTS.md guardrail 9).

import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  ArrowTopRightOnSquareIcon,
  ArrowUturnLeftIcon,
  BoltIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  DocumentDuplicateIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  LinkIcon,
  ListBulletIcon,
  SparklesIcon,
  WrenchIcon,
} from '@heroicons/react/24/outline';
import { cx } from '../../../../components/admin/console';
import { nzShortDay, nzTime, type CommitAuthor, type KindCount, type SectionKind } from '../data/changelog';

/** Each kind of change has its own colour; amber and red stay for warnings. */
export const KIND_STYLE: Record<SectionKind, { pill: string; head: string; dot: string; Icon: typeof SparklesIcon }> = {
  features: { pill: 'bg-cyan-400/10 text-cyan-300 border-cyan-400/30', head: 'text-cyan-300', dot: 'bg-cyan-400', Icon: SparklesIcon },
  fixes: { pill: 'bg-emerald-400/10 text-emerald-300 border-emerald-400/30', head: 'text-emerald-300', dot: 'bg-emerald-400', Icon: WrenchIcon },
  speedups: { pill: 'bg-violet-400/10 text-violet-300 border-violet-400/30', head: 'text-violet-300', dot: 'bg-violet-400', Icon: BoltIcon },
  reverts: { pill: 'bg-slate-400/10 text-slate-300 border-slate-400/30', head: 'text-slate-300', dot: 'bg-slate-400', Icon: ArrowUturnLeftIcon },
  breaking: { pill: 'bg-red-400/10 text-red-300 border-red-400/30', head: 'text-red-300', dot: 'bg-red-400', Icon: ExclamationTriangleIcon },
  other: { pill: 'bg-slate-400/10 text-slate-300 border-slate-400/30', head: 'text-slate-300', dot: 'bg-slate-400', Icon: ListBulletIcon },
};

export function KindPills({ counts, icons = true, className }: { counts: KindCount[]; icons?: boolean; className?: string }) {
  if (!counts.length) return null;
  return (
    <span className={cx('flex flex-wrap gap-1.5', className)}>
      {counts.map((k) => {
        const s = KIND_STYLE[k.kind];
        return (
          <span key={k.kind} className={cx('inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-[13px] font-semibold', s.pill)}>
            {icons && <s.Icon className="h-3.5 w-3.5" aria-hidden="true" />}
            {k.label}
          </span>
        );
      })}
    </span>
  );
}

export function Chevron({ open, className }: { open: boolean; className?: string }) {
  const Icon = open ? ChevronDownIcon : ChevronRightIcon;
  return <Icon className={cx('flex-none text-slate-400', className ?? 'h-5 w-5')} aria-hidden="true" />;
}

export function SectionLabel({ children }: { children: ReactNode }) {
  return <span className="text-[13px] font-bold uppercase tracking-[0.1em] text-slate-400">{children}</span>;
}

/**
 * The busiest tile lately: a small faded flame in the tile's bottom corner,
 * clear of the tick in the top one. The tile keeps its text clear of it
 * (BUSY_TEXT_ROOM).
 */
export function BusyMark() {
  return (
    <span role="img" aria-label="Busiest lately" className="pointer-events-none absolute bottom-2 right-2.5 select-none text-[13px] leading-none opacity-50">
      🔥
    </span>
  );
}

/**
 * Room on the busiest tile's text so a long last line never runs under the
 * flame; the name, at the top, takes it back (BUSY_NAME_ROOM).
 */
export const BUSY_TEXT_ROOM = 'pr-4';
export const BUSY_NAME_ROOM = '-mr-4';

/** The search's highlight, for a commit id or PR number that matched. */
export function Hit({ children }: { children: ReactNode }) {
  return <mark className="rounded-[3px] bg-yellow-400/25 px-0.5 text-amber-100">{children}</mark>;
}

/** Soft colours, none of them a kind's (cyan, emerald, violet) or a warning's (amber, red). */
const AUTHOR_TONES = [
  'bg-sky-400/15 text-sky-200 ring-sky-400/35',
  'bg-orange-400/15 text-orange-200 ring-orange-400/35',
  'bg-pink-400/15 text-pink-200 ring-pink-400/35',
  'bg-lime-400/15 text-lime-200 ring-lime-400/35',
  'bg-indigo-400/15 text-indigo-200 ring-indigo-400/35',
  'bg-teal-400/15 text-teal-200 ring-teal-400/35',
];

/** One colour per person, the same everywhere. */
export function authorTone(name: string): string {
  let h = 0;
  for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return AUTHOR_TONES[h % AUTHOR_TONES.length];
}

/** A small tooltip over `children`, shown at once on hover or keyboard focus. */
function Tip({ tip, label, className, children }: { tip: string; label: string; className?: string; children: ReactNode }) {
  return (
    <span className="group/tip relative flex-none">
      <span tabIndex={0} aria-label={label} className={cx('cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500', className)}>
        {children}
      </span>
      <span
        role="tooltip"
        className="pointer-events-none absolute bottom-full right-0 z-20 mb-2 whitespace-nowrap rounded-lg border border-slate-600 bg-slate-950 px-3 py-1.5 text-[13px] font-semibold text-white opacity-0 shadow-xl shadow-black/40 transition-opacity duration-100 group-hover/tip:opacity-100 group-focus-within/tip:opacity-100"
      >
        {tip}
      </span>
    </span>
  );
}

/** Who made a change: their initial in a circle, their name on hover or focus. */
export function AuthorMark({ author }: { author: CommitAuthor }) {
  return (
    <Tip
      tip={author.name}
      label={author.name}
      className={cx('flex h-[22px] w-[22px] select-none items-center justify-center rounded-full text-[13px] font-semibold leading-none ring-1 ring-inset', authorTone(author.name))}
    >
      {author.name.trim().charAt(0).toUpperCase()}
    </Tip>
  );
}

/** When a change went live: the time, its day on hover or focus. */
export function LiveTime({ at }: { at: string }) {
  const day = nzShortDay(at);
  return (
    // A fixed width, so the circles before it line up down the list.
    <Tip tip={day} label={`Went live ${day}, ${nzTime(at)}`} className="inline-block min-w-[60px] rounded text-right text-[13px] tabular-nums text-slate-500">
      {nzTime(at)}
    </Tip>
  );
}

export function CommitChip({ sha, url, hit = false }: { sha: string; url: string; hit?: boolean }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      title="Open this commit on GitHub"
      className="flex-none whitespace-nowrap rounded-md border border-slate-700 bg-slate-900/60 px-2 py-0.5 font-mono text-[13px] text-slate-400 transition-colors hover:border-cyan-400/45 hover:text-cyan-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500"
    >
      {hit ? <Hit>{sha}</Hit> : sha}
    </a>
  );
}

/** A change's PR. Phones leave it out of the lists (the commit is enough), but not when a search found it. */
export function PrChip({ number, url, hit = false }: { number: number; url: string; hit?: boolean }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      title="Open this pull request on GitHub"
      className={cx(
        'flex-none whitespace-nowrap rounded-md border border-slate-700 bg-slate-900/60 px-2 py-0.5 text-[13px] font-medium text-slate-400 transition-colors hover:border-cyan-400/45 hover:text-cyan-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500',
        hit ? 'inline-block' : 'hidden sm:inline-block',
      )}
    >
      {hit ? <Hit>#{number}</Hit> : `#${number}`}
    </a>
  );
}

export function ExternalLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="inline-flex min-h-[40px] items-center gap-1.5 rounded text-sm font-medium text-cyan-400 hover:text-cyan-300 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500"
    >
      {children}
      <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" aria-hidden="true" />
    </a>
  );
}

export function Notice({ tone, children, action }: { tone: 'warning' | 'error' | 'info'; children: ReactNode; action?: ReactNode }) {
  const box = { warning: 'bg-amber-500/[0.08] border-amber-500/25', error: 'bg-red-500/[0.08] border-red-500/25', info: 'bg-cyan-500/[0.07] border-cyan-500/20' }[tone];
  const Icon = tone === 'info' ? InformationCircleIcon : ExclamationTriangleIcon;
  const ink = { warning: 'text-amber-400', error: 'text-red-400', info: 'text-cyan-400' }[tone];
  return (
    <div role={tone === 'info' ? undefined : 'status'} className={cx('flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3 text-[15px] leading-relaxed text-slate-300', box)}>
      <Icon className={cx('mt-0.5 h-[18px] w-[18px] flex-none self-start', ink)} aria-hidden="true" />
      <div className="min-w-0 flex-1 basis-[260px]">{children}</div>
      {action}
    </div>
  );
}

type CopyState = 'idle' | 'copied' | 'failed';

function useCopy(): [CopyState, (text: string) => void] {
  const [state, setState] = useState<CopyState>('idle');
  const timer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(timer.current), []);
  const copy = (text: string) => {
    navigator.clipboard.writeText(text).then(() => setState('copied'), () => setState('failed'));
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setState('idle'), 2000);
  };
  return [state, copy];
}

function CopyStateText({ state }: { state: CopyState }) {
  return <span className="sr-only" aria-live="polite">{state === 'copied' ? 'Copied' : state === 'failed' ? "Couldn't copy" : ''}</span>;
}

/** "Copy": the label hides on phones, where the icon stands alone. */
export function CopyButton({ getText, ariaLabel, iconOnly = false }: { getText: () => string; ariaLabel: string; iconOnly?: boolean }) {
  const [state, copy] = useCopy();
  const icon = state === 'copied' ? <CheckIcon className="h-[18px] w-[18px] text-emerald-400" aria-hidden="true" /> : <DocumentDuplicateIcon className="h-[18px] w-[18px]" aria-hidden="true" />;
  const word = state === 'copied' ? 'Copied' : state === 'failed' ? "Couldn't copy" : 'Copy';
  return (
    <button
      type="button"
      onClick={() => copy(getText())}
      aria-label={ariaLabel}
      title={ariaLabel}
      className={cx(
        'inline-flex flex-none items-center justify-center gap-2 rounded-lg text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500',
        iconOnly
          ? 'h-11 w-11 text-slate-400 hover:bg-slate-700/55 hover:text-white'
          : 'h-11 min-w-[44px] border border-slate-700 bg-slate-800/60 px-3 text-slate-200 hover:border-cyan-400/45 hover:bg-slate-800 hover:text-white sm:px-3.5',
      )}
    >
      {icon}
      {!iconOnly && <span className="hidden sm:inline">{word}</span>}
      <CopyStateText state={state} />
    </button>
  );
}

export function CopyLinkButton({ getUrl, ariaLabel }: { getUrl: () => string; ariaLabel: string }) {
  const [state, copy] = useCopy();
  return (
    <button
      type="button"
      onClick={() => copy(getUrl())}
      aria-label={ariaLabel}
      title={ariaLabel}
      className="inline-flex h-11 w-11 flex-none items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-700/55 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500"
    >
      {state === 'copied' ? <CheckIcon className="h-[18px] w-[18px] text-emerald-400" aria-hidden="true" /> : <LinkIcon className="h-[18px] w-[18px]" aria-hidden="true" />}
      <CopyStateText state={state} />
    </button>
  );
}

export function TextButton({ onClick, children }: { onClick: () => void; children: string }) {
  return (
    <button type="button" onClick={onClick} className="min-h-[36px] rounded-md px-2.5 text-sm font-medium text-slate-400 hover:bg-slate-800 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500">
      {children}
    </button>
  );
}

export function RetryButton({ onClick, busy }: { onClick: () => void; busy: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className="min-h-[40px] rounded-lg border border-slate-700 bg-slate-800/60 px-3.5 text-sm font-semibold text-slate-200 hover:bg-slate-800 hover:text-white disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500"
    >
      {busy ? 'Trying…' : 'Try again'}
    </button>
  );
}

/** Placeholder rows in the page's own shape while the changelog loads. */
export function LoadingTimeline() {
  return (
    <div className="space-y-3" aria-busy="true" aria-label="Loading the changelog">
      <div className="h-[168px] animate-pulse rounded-2xl border border-slate-700/60 bg-slate-800/40" />
      {[0, 1, 2].map((i) => (
        <div key={i} className="sm:grid sm:grid-cols-[76px_minmax(0,1fr)] sm:gap-4">
          <div className="hidden sm:block" />
          <div className="h-[92px] animate-pulse rounded-2xl border border-slate-700/60 bg-slate-800/40" />
        </div>
      ))}
    </div>
  );
}
